import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { 
  getFirestore, 
  initializeFirestore, 
  persistentLocalCache, 
  persistentMultipleTabManager,
  enableNetwork, 
  disableNetwork 
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// Your Firebase config - Update these with your actual Firebase project details
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "your-api-key",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "japacounter-72f8f.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "japacounter-72f8f",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "japacounter-72f8f.appspot.com",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "your-sender-id",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "your-app-id"
};

// Debug logging for production
console.log('🔧 Firebase Config Debug:', {
  apiKey: firebaseConfig.apiKey ? 'Set ✅' : 'Missing ❌',
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId,
  environment: process.env.NODE_ENV,
  currentDomain: window.location.hostname
});

// Check if Firebase is properly configured
const isFirebaseConfigured = () => {
  const configured = firebaseConfig.apiKey && 
         firebaseConfig.projectId &&
         firebaseConfig.apiKey !== "your-api-key";
  
  console.log('🔥 Firebase Configuration Status:', configured ? '✅ Configured' : '❌ Not Configured');
  return configured;
};

// Initialize Firebase (single instance)
let app;
let db;
let auth;
let googleProvider;
let storage;

try {
  console.log('🔥 Initializing Firebase...');
  
  // Initialize Firebase App
  app = initializeApp(firebaseConfig);
  
  // Initialize Firebase Auth
  auth = getAuth(app);
  
  // Initialize Google Auth Provider
  googleProvider = new GoogleAuthProvider();
  googleProvider.addScope('profile');
  googleProvider.addScope('email');
  googleProvider.setCustomParameters({
    prompt: 'select_account'
  });
  
  // Initialize Firestore with modern cache (replaces deprecated enableIndexedDbPersistence)
  if (isFirebaseConfigured()) {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager()
      })
    });
    console.log('✅ Firebase initialized with multi-tab offline persistence');
  } else {
    db = getFirestore(app);
    console.warn('⚠️ Firebase using fallback configuration - please check your .env file');
  }
  
  // Initialize Storage
  storage = getStorage(app);
  
} catch (error) {
  console.error('💥 Firebase initialization error:', error.message);
  
  // Fallback initialization for development
  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  
  if (!db) {
    db = getFirestore(app);
  }
  
  if (!auth) {
    auth = getAuth(app);
    googleProvider = new GoogleAuthProvider();
  }
  
  if (!storage) {
    storage = getStorage(app);
  }
  
  console.log('⚠️ Using fallback Firebase configuration');
}

// Network state management
export const enableFirestoreNetwork = () => {
  console.log('🌐 Enabling Firestore network...');
  return enableNetwork(db);
};

export const disableFirestoreNetwork = () => {
  console.log('📴 Disabling Firestore network...');
  return disableNetwork(db);
};

// Network status checker
export const checkNetworkStatus = () => {
  return navigator.onLine;
};

// Auto-reconnect when network comes back (only in browser)
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('🌐 Network connected - re-enabling Firestore');
    enableFirestoreNetwork().catch(error => {
      console.error('❌ Failed to re-enable network:', error);
    });
  });

  window.addEventListener('offline', () => {
    console.log('📴 Network disconnected - Firestore will work offline with cached data');
  });
}

// Export Firebase services
export { db, auth, googleProvider, storage, isFirebaseConfigured };
export default app; 